import { useState, useMemo, useEffect, useRef } from 'react';
import {
  ChevronDown,
  Sparkles,
  X,
  Loader2,
  Star,
  CalendarDays,
  Building2,
  Search,
  Download,
} from 'lucide-react';
import type { Transaction } from '../types';
import TransactionDetail from './TransactionDetail';
import { useSmartSearch, useStarTransaction } from '../hooks/useTransactions';
import { formatAccountName } from '../utils/accounts';
import {
  propertyStatusLabel,
  propertyStatusTooltip,
  propertyStatusTone,
  partyLabel,
} from '../lib/enums';
import { cn } from '../lib/cn';
import {
  DataTable,
  Pagination,
  Tag,
  PartyRail,
  PartyLegend,
  Money,
  DateText,
  ConfidenceDot,
  IconButton,
  Tooltip,
  Toolbar,
  ToolbarChip,
  Button,
  Input,
  Select,
  EmptyState,
  type ColumnDef,
  type Party,
} from './ui';

type PartyFilter = 'client' | 'opposing' | 'third_party' | 'joint' | '';

interface TransactionTableProps {
  transactions: Transaction[];
  onOverride: (transaction: Transaction) => void;
  caseId?: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
  totalCount?: number;
  accountOptions?: string[];
  accountFilters?: string[];
  onToggleAccount?: (label: string) => void;
  onClearAccounts?: () => void;
  partyFilter?: PartyFilter;
  onPartyChange?: (p: PartyFilter) => void;
  afterSeparation?: boolean;
  onAfterSeparationChange?: (v: boolean) => void;
  separationDate?: string | null;
  onExport?: () => void;
  exporting?: boolean;
  /** Work-product boundary: default export excludes waste/signal/starred columns */
  includeWorkProduct?: boolean;
  onToggleWorkProduct?: (v: boolean) => void;
}

type SortField = 'date' | 'description' | 'amount' | 'property_status' | 'confidence_score' | 'bank_label' | 'transaction_type';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const fmt = new Intl.NumberFormat('en-US');

const PARTY_ORDER: Party[] = ['client', 'opposing', 'third_party', 'joint'];

function fmtShortDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}

export default function TransactionTable({
  transactions,
  onOverride,
  caseId,
  clientName,
  opposingPartyName,
  totalCount,
  accountOptions = [],
  accountFilters = [],
  onToggleAccount,
  onClearAccounts,
  partyFilter = '',
  onPartyChange,
  afterSeparation = false,
  onAfterSeparationChange,
  separationDate,
  onExport,
  exporting = false,
  includeWorkProduct = false,
  onToggleWorkProduct,
}: TransactionTableProps) {
  const showFilterControls = Boolean(onToggleAccount);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [showDatePopover, setShowDatePopover] = useState(false);
  const datePopoverRef = useRef<HTMLDivElement>(null);
  const [filterWaste, setFilterWaste] = useState<string>('');
  const [filterPoints, setFilterPoints] = useState<boolean>(false);
  const [filterStarred, setFilterStarred] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [smartSearchEnabled, setSmartSearchEnabled] = useState(false);
  const [smartResults, setSmartResults] = useState<Transaction[] | null>(null);
  const [smartSummary, setSmartSummary] = useState<string | null>(null);

  const smartSearch = useSmartSearch(caseId);
  const starTransaction = useStarTransaction(caseId);

  const handleSmartSearch = async () => {
    if (!searchQuery.trim() || !caseId) return;
    try {
      const result = await smartSearch.mutateAsync(searchQuery.trim());
      setSmartResults(result.transactions);
      setSmartSummary(result.search_summary);
    } catch {
      setSmartResults([]);
      setSmartSummary('Search failed — try again');
    }
  };

  const clearSmartResults = () => {
    setSmartResults(null);
    setSmartSummary(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && smartSearchEnabled) {
      e.preventDefault();
      handleSmartSearch();
    }
  };

  useEffect(() => {
    if (!showDatePopover) return;
    const handler = (e: MouseEvent) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node)) {
        setShowDatePopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDatePopover]);

  const baseTransactions = smartResults !== null ? smartResults : transactions;

  const starredCount = useMemo(() => baseTransactions.filter((t) => t.is_starred).length, [baseTransactions]);

  const pointsCount = useMemo(() => baseTransactions.filter((t) => t.transaction_type === 'shop_with_points').length, [baseTransactions]);

  const filtered = useMemo(() => {
    let result = [...baseTransactions];
    if (filterDateFrom) {
      result = result.filter((t) => t.date >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter((t) => t.date <= filterDateTo);
    }
    if (filterWaste === 'waste') {
      result = result.filter((t) => t.waste_flag);
    } else if (filterWaste === 'no-waste') {
      result = result.filter((t) => !t.waste_flag);
    }
    if (filterPoints) {
      result = result.filter((t) => t.transaction_type === 'shop_with_points');
    }
    if (filterStarred) {
      result = result.filter((t) => t.is_starred);
    }
    if (searchQuery && !smartSearchEnabled) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.description.toLowerCase().includes(q) ||
          (t.merchant_name && t.merchant_name.toLowerCase().includes(q))
      );
    }
    return result;
  }, [baseTransactions, filterDateFrom, filterDateTo, filterWaste, filterPoints, filterStarred, searchQuery, smartSearchEnabled]);

  const sorted = useMemo(() => {
    const q = (searchQuery && !smartSearchEnabled) ? searchQuery.toLowerCase() : null;

    const relevance = (t: Transaction): number => {
      if (!q) return 0;
      const desc = t.description.toLowerCase();
      const merchant = (t.merchant_name || '').toLowerCase();
      if (desc.startsWith(q)) return 3;
      if (desc.includes(q)) return 2;
      if (merchant.includes(q)) return 1;
      return 0;
    };

    return [...filtered].sort((a, b) => {
      if (q) {
        const relDiff = relevance(b) - relevance(a);
        if (relDiff !== 0) return relDiff;
      }
      let cmp = 0;
      switch (sortField) {
        case 'date':
          cmp = (a.date || '').localeCompare(b.date || '');
          break;
        case 'description':
          cmp = a.description.localeCompare(b.description);
          break;
        case 'amount':
          cmp = a.amount - b.amount;
          break;
        case 'property_status':
          cmp = a.property_status.localeCompare(b.property_status);
          break;
        case 'confidence_score':
          cmp = (a.confidence_score || 0) - (b.confidence_score || 0);
          break;
        case 'bank_label':
          cmp = a.bank_label.localeCompare(b.bank_label);
          break;
        case 'transaction_type':
          cmp = (a.transaction_type || '').localeCompare(b.transaction_type || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir, searchQuery, smartSearchEnabled]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [accountFilters, partyFilter, afterSeparation, filterDateFrom, filterDateTo, filterWaste, filterPoints, filterStarred, searchQuery, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  const rangeStart = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, sorted.length);

  const presentParties = useMemo<Party[]>(() => {
    const present = new Set(sorted.map((t) => t.source_party).filter(Boolean) as Party[]);
    return PARTY_ORDER.filter((p) => present.has(p));
  }, [sorted]);

  const handleToggleStar = (txn: Transaction) => {
    if (smartResults !== null) {
      setSmartResults((prev) =>
        prev?.map((t) =>
          t.id === txn.id ? { ...t, is_starred: !txn.is_starred } : t
        ) ?? prev
      );
    }
    starTransaction.mutate({ transactionId: txn.id, currentStarred: txn.is_starred });
  };

  const columns: ColumnDef<Transaction>[] = [
    // 1 ── Party rail: provenance as a 3px band; the party is NAMED once in the legend above.
    {
      id: 'party',
      header: 'Produced by',
      headerSrOnly: true,
      width: 'w-1',
      cellClassName: 'p-0 h-11',
      cell: (t) => (
        <PartyRail
          party={t.source_party}
          name={partyLabel(t.source_party, clientName, opposingPartyName)}
        />
      ),
    },
    // 2 ── Star. 28px target, aria-pressed; cell clicks never toggle expansion.
    {
      id: 'star',
      header: 'Starred',
      headerSrOnly: true,
      width: 'w-9',
      align: 'center',
      stopPropagation: true,
      cell: (t) => (
        <IconButton
          icon={Star}
          size="sm"
          variant="ghost"
          aria-pressed={t.is_starred}
          aria-label={t.is_starred ? 'Unstar transaction' : 'Star transaction'}
          className={
            t.is_starred
              ? 'text-clay-400 [&_svg]:fill-clay-400'
              : 'text-ink-faint hover:text-clay-400'
          }
          onClick={() => handleToggleStar(t)}
        />
      ),
    },
    // 3 ── Date. Zero-padded + tabular: a straight left edge.
    {
      id: 'date',
      header: 'Date',
      width: 'w-28',
      sortable: true,
      sortKey: 'date',
      cell: (t) => <DateText value={t.date} className="text-ink-soft" />,
    },
    {
      id: 'account',
      header: 'Account',
      width: 'w-36',
      sortable: true,
      sortKey: 'bank_label',
      cell: (t) => (
        <Tooltip content={formatAccountName(t.bank_label)} whenTruncated>
          <span className="block truncate text-ink-soft">{formatAccountName(t.bank_label)}</span>
        </Tooltip>
      ),
    },
    // 4 ── Description: the ONLY flex column, with a floor so it can't be crushed.
    {
      id: 'description',
      header: 'Description',
      width: 'flex',
      minWidth: 'min-w-72',
      sortable: true,
      sortKey: 'description',
      cell: (t) => (
        <div className="flex min-w-0 items-center gap-2">
          <Tooltip content={t.description} whenTruncated>
            <span className="min-w-0 truncate">{t.description}</span>
          </Tooltip>
          {t.transaction_type === 'shop_with_points' && (
            <Tag
              tone="info"
              size="sm"
              title="Purchased with rewards points — excluded from dollar reconciliation."
            >
              Points
            </Tag>
          )}
        </div>
      ),
    },
    // 5 ── Amount: right-aligned tabular figures, U+2212, pinned 2 decimals.
    {
      id: 'amount',
      header: 'Amount',
      width: 'w-32',
      numeric: true,
      sortable: true,
      sortKey: 'amount',
      cell: (t) => <Money value={t.amount} tone="auto" />,
    },
    // 6 ── Exception gutter: low confidence + manual override, dots only.
    //      Exact % lives in the expanded TransactionDetail.
    {
      id: 'flags',
      header: 'Needs attention',
      headerSrOnly: true,
      width: 'w-6',
      align: 'center',
      cell: (t) => (
        <span className="inline-flex items-center justify-center gap-1">
          <ConfidenceDot score={t.confidence_score} />
          {t.is_override && (
            <span
              title="Classification manually overridden by the firm."
              role="img"
              aria-label="Manually overridden"
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-clay-500"
            />
          )}
        </span>
      ),
    },
    {
      id: 'waste',
      header: 'Waste',
      width: 'w-20',
      cell: (t) =>
        t.waste_flag ? (
          <Tag
            size="sm"
            tone={
              t.waste_severity === 'high'
                ? 'danger'
                : t.waste_severity === 'medium'
                  ? 'warn'
                  : 'info'
            }
          >
            {t.waste_severity || 'yes'}
          </Tag>
        ) : null,
    },
    // 7 ── Status: ONE fact, ONE tag, ONE line.
    {
      id: 'status',
      header: 'Status',
      width: 'w-28',
      sortable: true,
      sortKey: 'property_status',
      cellClassName: 'whitespace-nowrap',
      cell: (t) => (
        <Tag
          size="sm"
          tone={propertyStatusTone(t.property_status)}
          title={propertyStatusTooltip(t.property_status) || t.property_status_display}
        >
          {propertyStatusLabel(t.property_status, 'short')}
        </Tag>
      ),
    },
  ];

  const shownCount = totalCount ?? transactions.length;

  return (
    <div className="space-y-4">
      {smartSummary && (
        <div className="flex items-center gap-2 rounded-control border border-good bg-good-soft px-3 py-2 text-sm">
          <Sparkles size={16} className="shrink-0 text-good" aria-hidden="true" />
          <span className="flex-1 text-ink">
            <span className="font-medium">Agentic Search:</span> {smartSummary}
            {smartResults && <span className="ml-1 text-ink-soft">({smartResults.length} found)</span>}
          </span>
          <IconButton
            icon={X}
            size="xs"
            variant="ghost"
            aria-label="Clear Agentic Search"
            onClick={clearSmartResults}
          />
        </div>
      )}

      <Toolbar
        size="md"
        end={
          showFilterControls && onExport ? (
            <>
              {onToggleWorkProduct && (
                <label
                  className="inline-flex cursor-pointer select-none items-center gap-2 text-xs text-ink-soft"
                  title="Waste flags, hidden-asset signals, and starred rows are the firm's analysis. Off (default): the export is safe to produce in discovery. On: the file is stamped ATTORNEY WORK PRODUCT — DO NOT PRODUCE."
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                  <input
                    type="checkbox"
                    checked={!!includeWorkProduct}
                    onChange={(e) => onToggleWorkProduct(e.target.checked)}
                    className="h-4 w-4 rounded border-border-strong accent-danger"
                  />
                  Include work product
                  {includeWorkProduct && (
                    <Tag size="sm" tone="danger">
                      DO NOT PRODUCE
                    </Tag>
                  )}
                </label>
              )}
              <Button
                variant="secondary"
                size="md"
                icon={Download}
                loading={exporting}
                onClick={onExport}
                disabled={exporting || shownCount === 0}
                title={
                  accountFilters.length > 0 || partyFilter || afterSeparation
                    ? 'Download Excel of currently filtered transactions'
                    : 'Download every transaction in this case as an Excel workbook'
                }
              >
                {exporting ? 'Preparing…' : 'Download Excel'}
              </Button>
            </>
          ) : undefined
        }
      >
        <Input
          type="text"
          size="md"
          placeholder={smartSearchEnabled ? 'Agentic Search... (press Enter)' : 'Search transactions...'}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            clearSmartResults();
          }}
          onKeyDown={handleSearchKeyDown}
          className={cn('w-72', smartSearchEnabled && 'border-green-700')}
          trailing={
            <IconButton
              icon={smartSearch.isPending ? Loader2 : Sparkles}
              size="xs"
              variant="ghost"
              aria-label={smartSearchEnabled ? 'Switch to text search' : 'Switch to Agentic Search'}
              aria-pressed={smartSearchEnabled}
              onClick={() => {
                setSmartSearchEnabled(!smartSearchEnabled);
                if (smartSearchEnabled) {
                  clearSmartResults();
                }
              }}
              className={cn(
                smartSearch.isPending && '[&_svg]:animate-spin',
                smartSearchEnabled ? 'text-green-700 bg-green-100' : 'text-ink-faint'
              )}
            />
          }
        />

        {showFilterControls && accountOptions.length > 1 && (
          <div className="relative flex items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              icon={Building2}
              onClick={() => setAccountMenuOpen((v) => !v)}
            >
              {accountFilters.length === 0
                ? 'All Accounts'
                : accountFilters.length === 1
                  ? formatAccountName(accountFilters[0])
                  : `${accountFilters.length} accounts`}
              <ChevronDown
                size={16}
                aria-hidden="true"
                className={cn(
                  'transition-transform duration-[var(--dur-fast)]',
                  accountMenuOpen && 'rotate-180'
                )}
              />
            </Button>
            {accountMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAccountMenuOpen(false)} />
                <div className="absolute left-0 top-full z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-overlay border border-border bg-cream-light py-1 shadow-overlay">
                  {accountOptions.map((label) => {
                    const checked = accountFilters.includes(label);
                    return (
                      <label
                        key={label}
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-cream"
                      >
                        {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleAccount?.(label)}
                          className="h-4 w-4 rounded border-border-strong"
                        />
                        <span className="truncate">{formatAccountName(label)}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
            {accountFilters.length > 0 && (
              <IconButton
                icon={X}
                size="md"
                variant="secondary"
                aria-label="Clear account filter"
                onClick={() => onClearAccounts?.()}
              />
            )}
          </div>
        )}

        {showFilterControls && (
          <Select
            size="md"
            value={partyFilter}
            onChange={(e) => onPartyChange?.(e.target.value as PartyFilter)}
            options={[
              { value: '', label: 'All production sources' },
              { value: 'client', label: `Produced by ${clientName || 'client'}` },
              { value: 'opposing', label: `Produced by ${opposingPartyName || 'opposing side'}` },
              { value: 'third_party', label: 'Produced by third party' },
              { value: 'joint', label: 'Joint (legacy)' },
            ]}
          />
        )}

        <Select
          size="md"
          value={filterWaste}
          onChange={(e) => setFilterWaste(e.target.value)}
          options={[
            { value: '', label: 'All Transactions' },
            { value: 'waste', label: 'Potential Waste Only' },
            { value: 'no-waste', label: 'Non-Waste Only' },
          ]}
        />

        <ToolbarChip
          selected={filterStarred}
          icon={Star}
          count={starredCount > 0 ? starredCount : undefined}
          onClick={() => setFilterStarred(!filterStarred)}
        >
          Starred
        </ToolbarChip>

        {pointsCount > 0 && (
          <ToolbarChip
            selected={filterPoints}
            count={filterPoints ? pointsCount : undefined}
            onClick={() => setFilterPoints(!filterPoints)}
          >
            Points
          </ToolbarChip>
        )}

        <div className="relative" ref={datePopoverRef}>
          <ToolbarChip
            selected={Boolean(filterDateFrom || filterDateTo)}
            icon={CalendarDays}
            onClick={() => setShowDatePopover((v) => !v)}
          >
            {filterDateFrom || filterDateTo
              ? `${filterDateFrom ? fmtShortDate(filterDateFrom) : '…'} – ${filterDateTo ? fmtShortDate(filterDateTo) : '…'}`
              : 'Date Range'}
            {(filterDateFrom || filterDateTo) && (
              <span
                role="button"
                aria-label="Clear date range"
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
                className="ml-1 opacity-70 hover:opacity-100"
              >
                <X size={12} aria-hidden="true" />
              </span>
            )}
          </ToolbarChip>
          {showDatePopover && (
            <div className="absolute right-0 top-full z-30 mt-1 flex min-w-48 flex-col gap-2 rounded-overlay border border-border bg-cream-light p-3 shadow-overlay">
              <Input
                type="date"
                size="sm"
                label="From"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
              <Input
                type="date"
                size="sm"
                label="To"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
              {(filterDateFrom || filterDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 self-start"
                  onClick={() => {
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>

        {showFilterControls && separationDate && (
          <ToolbarChip
            selected={afterSeparation}
            onClick={() => onAfterSeparationChange?.(!afterSeparation)}
            title={`Filter to transactions on or after ${separationDate}`}
          >
            After separation
          </ToolbarChip>
        )}
      </Toolbar>

      {/* The party is named ONCE, here — not once per row down the page. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PartyLegend
          parties={presentParties}
          clientName={clientName}
          opposingName={opposingPartyName}
        />
        <span className="ml-auto text-sm tabular-nums text-ink-soft">
          Showing {fmt.format(rangeStart)}–{fmt.format(rangeEnd)} of {fmt.format(sorted.length)} transactions
          {sorted.length !== shownCount && (
            <span className="text-ink-faint"> (filtered from {fmt.format(shownCount)})</span>
          )}
        </span>
      </div>

      <DataTable
        caption="Transactions, with date, account, description, amount, waste flag and property characterization"
        rows={paginatedRows}
        columns={columns}
        rowKey={(t) => t.id}
        density="default"
        minWidth={920}
        stickyHeader
        sort={{ key: sortField, dir: sortDir }}
        onSortChange={(s) => {
          setSortField(s.key as SortField);
          setSortDir(s.dir);
        }}
        onRowClick={(t) => setExpandedId(expandedId === t.id ? null : t.id)}
        rowTone={(t) => (t.is_override ? 'flagged' : 'default')}
        expandedKey={expandedId}
        onExpandedChange={(k) => setExpandedId(k === null ? null : Number(k))}
        renderExpanded={(t) => <TransactionDetail transaction={t} onOverride={onOverride} />}
        empty={
          filterStarred ? (
            <EmptyState
              size="inline"
              icon={Star}
              title="No starred transactions"
              description="Click the star next to any transaction to add it here."
            />
          ) : (
            <EmptyState size="inline" icon={Search} title="No transactions found" />
          )
        }
      />

      {sorted.length > 0 && (
        <Pagination
          page={currentPage}
          pageCount={totalPages}
          pageSize={pageSize}
          total={sorted.length}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          noun="transactions"
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}
